/** Report list metadata may omit display fields before its body is hydrated. */
export function reportText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

export function normalizedSiteName(value: unknown): string {
  return reportText(value).toLowerCase().replace(/\s+/g, ' ');
}

type SiteIdentity = { id: unknown; name?: unknown; reportIds?: unknown };
type ReportIdentity = { id: unknown; siteId?: unknown; siteName?: unknown };

function hasSiteId(report: ReportIdentity): boolean {
  return report.siteId != null && report.siteId !== '';
}

/** Explicit IDs are authoritative; a foreign or malformed ID never uses a name. */
export function reportBelongsToSite(site: SiteIdentity, report: ReportIdentity): boolean {
  if (hasSiteId(report)) {
    return typeof report.siteId === 'string' && reportText(report.siteId).length > 0
      && report.siteId === site.id;
  }
  if (Array.isArray(site.reportIds) && site.reportIds.includes(report.id)) return true;
  const name = normalizedSiteName(site.name);
  return name.length > 0 && normalizedSiteName(report.siteName) === name;
}

/** Prefer a persisted report link over the legacy display-name association. */
export function siteForReport<T extends SiteIdentity>(sites: readonly T[], report: ReportIdentity | undefined): T | undefined {
  if (!report) return undefined;
  if (!hasSiteId(report)) {
    const linked = sites.find(site => Array.isArray(site.reportIds) && site.reportIds.includes(report.id));
    if (linked) return linked;
  }
  return sites.find(site => reportBelongsToSite(site, report));
}

/** Unknown identity stays isolated instead of merging every nameless row. */
export function reportSiteGroupKey(report: ReportIdentity): string {
  if (hasSiteId(report)) {
    return typeof report.siteId === 'string' && reportText(report.siteId).length > 0
      ? `site:${report.siteId}` : `report:${report.id}`;
  }
  const name = normalizedSiteName(report.siteName);
  return name ? `name:${name}` : `report:${report.id}`;
}

/** Legacy site derivation needs a real name; pending metadata remains in reports. */
export function groupLegacySiteReports<T extends ReportIdentity>(reports: readonly T[]): Map<string, T[]> {
  const groups = new Map<string, T[]>();
  for (const report of reports) {
    if (hasSiteId(report) && (typeof report.siteId !== 'string' || !reportText(report.siteId))) continue;
    const key = reportSiteGroupKey(report);
    const group = groups.get(key) ?? [];
    group.push(report);
    groups.set(key, group);
  }
  for (const [key, group] of groups) {
    if (!group.some(report => reportText(report.siteName))) groups.delete(key);
  }
  return groups;
}
